import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Button, Stack, Typography } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

import { AccessGate, KernelLockup, Loading, resume, signIn, signOut, theme } from "@lopie/kernel-ui";

import App from "./App.jsx";

/**
 * Sign in as this app, then hand the token down.
 *
 * The same flow every app on this kernel uses, from the kit and not
 * reimplemented. This instance signs in with **its own** Cognito client,
 * written into `config.js` by the composition, so the manifest the kernel
 * resolves is Insights' and not the console's.
 */
// A link in a report email is `#report=<id>`, and a visit that has to sign in first would lose it on the way
// to the hosted login and back. Kept in this tab's session storage across that trip and nowhere else.
const LINK = "insights.link";
function keepLink() {
  try {
    if (/^#report=[A-Za-z0-9]+$/.test(window.location.hash)) sessionStorage.setItem(LINK, window.location.hash);
  } catch { /* none */ }
}
function restoreLink() {
  try {
    const hash = sessionStorage.getItem(LINK);
    if (!hash) return;
    sessionStorage.removeItem(LINK);
    if (!window.location.hash) window.history.replaceState(null, "", window.location.pathname + window.location.search + hash);
  } catch { /* none */ }
}

function Shell() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [deliberate, setDeliberate] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // A deliberate sign-out must not be bounced straight back into a login page.
    if (new URLSearchParams(window.location.search).has("signedout")) {
      setDeliberate(true);
      setReady(true);
      return;
    }
    resume()
      .then((s) => {
        setSession(s);
        if (s) restoreLink();
        if (!s) { keepLink(); signIn(); }
      })
      .catch((e) => setError(e.message))
      .finally(() => setReady(true));
  }, []);

  if (!ready) return <Loading label="Restoring your session" minHeight="100vh" />;

  if (!session) {
    return (
      <Stack spacing={2} sx={{ p: 4, alignItems: "flex-start" }}>
        <KernelLockup />
        <Typography variant="h1">Insights</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "60ch" }}>
          {deliberate
            ? "You are signed out."
            : error || "This app signs you in through the same kernel as everything else."}
        </Typography>
        <Button variant="contained" onClick={signIn}>
          Sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* The two pixels every Kernel surface carries. */}
      <Box sx={{ height: 2, bgcolor: "primary.main" }} />
      {/* Insights is judged by the person alone (`access_needs.PERSON_SCOPED_APPS`),
          so this gate is let through by the kernel for anyone. It stays so that
          the day that stops being true, the person is told what is missing and
          the app is never mounted half-working. `appType` is this app's name
          to the kernel. */}
      <AccessGate
        appType="insights"
        title="Insights"
        token={session.token}
        onSignOut={() => signOut()}
      >
        <App token={session.token} claims={session.claims} onSignOut={() => signOut()} />
      </AccessGate>
    </Box>
  );
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ThemeProvider theme={theme} defaultMode="system">
      {/* v9: `enableColorScheme` lives on CssBaseline, not on the provider. */}
      <CssBaseline enableColorScheme />
      <Shell />
    </ThemeProvider>
  </React.StrictMode>,
);
